import { useMemo, useState } from 'react';
import { X, Plus, MessageSquare, Trash2, Search } from 'lucide-react';
import type { ThreadItem } from '../services/threadService';
import { sidebar } from './sidebarStyles';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './ui/sheet';
import { cn } from './ui/utils';
import { isSmartModeActive } from '../api/smartApi';

function formatRelativeTime(ts: string): string {
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    const now = new Date();
    const diffM = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (diffM < 1) return 'Just now';
    if (diffM < 60) return `${diffM}m ago`;
    const diffH = Math.floor(diffM / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return ts;
  }
}

export interface PatientOption {
  name: string;
  mrn: string;
  status?: string;
  risk?: string;
}

interface ThreadsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** When true, render as in-flow sidebar (no overlay); when false, render as overlay drawer */
  asSidebar?: boolean;
  sidebarWidth?: number;
  threadsByPatient: { patientLabel: string; threads: ThreadItem[] }[];
  currentThreadId: string;
  /** The thread currently open (kept for call-site compatibility) */
  currentThread: ThreadItem | undefined;
  currentPatient: PatientOption | null;
  onSelectThread: (threadId: string) => void;
  onCreateThread: () => void;
  /** Create a brand new thread explicitly for the chosen patient */
  onCreateThreadForPatient: (patient: PatientOption) => void;
  /** Open patient picker to choose patient for a new thread (e.g. modal) */
  onOpenPatientPickerForNewThread?: () => void;
  onDeleteThread: (threadId: string) => void;
}

function ThreadRow({
  thread,
  active,
  onSelect,
  onDelete,
}: {
  thread: ThreadItem;
  active: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-1.5 rounded-xl border px-2.5 py-2 transition-colors',
          active
            ? 'border-primary/35 bg-primary/10'
            : 'border-sidebar-border hover:bg-sidebar-accent/50',
        )}
      >
        <button
          type="button"
          onClick={onSelect}
          aria-current={active ? 'true' : undefined}
          className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
        >
          <span
            className={cn(
              'truncate text-[12px] leading-snug',
              active ? 'font-medium text-primary' : 'font-medium text-sidebar-foreground',
            )}
          >
            {thread.title}
          </span>
          <span className="text-[10px] text-text-tertiary">{formatRelativeTime(thread.timestamp)}</span>
        </button>
        {confirming ? (
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onDelete}
              className="rounded px-1.5 py-0.5 text-[10px] font-medium text-destructive-text hover:bg-destructive-subtle"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded px-1.5 py-0.5 text-[10px] text-text-tertiary hover:bg-sidebar-accent/60"
            >
              Keep
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="shrink-0 rounded p-1 text-text-tertiary opacity-0 transition-opacity hover:bg-sidebar-accent/60 hover:text-destructive-text focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={`Delete conversation ${thread.title}`}
            title="Delete conversation"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * The clinician's conversation list — one list, one truth.
 *
 * The old drawer showed the SAME conversation up to three times ("Active
 * patient" card, "Recent chats", and an "All patients" folder tree) even
 * though a SMART launch locks the workspace to exactly one patient. Now:
 * a single chronological list of THIS doctor's conversations with THIS
 * patient (the data underneath is owner-scoped server-side — other
 * clinicians' conversations never reach this component), with search only
 * when the list is long enough to need it. Outside SMART mode the same flat
 * list is grouped by patient with plain section headers.
 */
export function ThreadsDrawer({
  open,
  onClose,
  asSidebar = false,
  sidebarWidth,
  threadsByPatient,
  currentThreadId,
  currentPatient,
  onSelectThread,
  onCreateThread,
  onOpenPatientPickerForNewThread,
  onDeleteThread,
}: ThreadsDrawerProps) {
  const [search, setSearch] = useState('');
  const patientLocked = isSmartModeActive();

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const bySearch = q
      ? threadsByPatient
          .map((g) => ({ ...g, threads: g.threads.filter((t) => t.title.toLowerCase().includes(q)) }))
          .filter((g) => g.threads.length > 0)
      : threadsByPatient;
    return bySearch.map((g) => ({
      ...g,
      threads: [...g.threads].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      ),
    }));
  }, [threadsByPatient, search]);

  const totalThreads = useMemo(
    () => threadsByPatient.reduce((n, g) => n + g.threads.length, 0),
    [threadsByPatient],
  );
  const flatThreads = useMemo(() => groups.flatMap((g) => g.threads), [groups]);

  // In-flow sidebar mode has no Radix state machine to manage visibility.
  // Overlay mode keeps the Sheet root mounted and drives `open`, so Radix can
  // run its close sequence (exit animation + focus restore to the trigger).
  if (asSidebar && !open) return null;

  const emptyCopy = currentPatient?.name
    ? `No conversations with ${currentPatient.name} yet — start one below.`
    : 'No conversations yet — start one below.';

  const subtitle =
    patientLocked && currentPatient?.name
      ? `Yours with ${currentPatient.name} — only you can see these`
      : 'Your conversations — only you can see these';
  const subtitleClass = 'truncate text-[10px] text-text-tertiary normal-case tracking-normal';

  // In overlay mode the title/subtitle double as the dialog's accessible
  // name/description, so they must be Radix Title/Description primitives.
  const header = (overlay: boolean) => (
    <div className={sidebar.header}>
      <div className="min-w-0">
        {overlay ? (
          <SheetTitle className={sidebar.title}>Conversations</SheetTitle>
        ) : (
          <h2 className={sidebar.title}>Conversations</h2>
        )}
        {overlay ? (
          <SheetDescription className={subtitleClass}>{subtitle}</SheetDescription>
        ) : (
          <p className={subtitleClass}>{subtitle}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className={sidebar.closeButton}
        aria-label="Close conversations"
        title="Close conversations"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );

  const body = (
    <div className={`${sidebar.body} ${sidebar.bodyPad} flex flex-col gap-2.5`}>
        <button
          type="button"
          onClick={() => {
            if (patientLocked || !onOpenPatientPickerForNewThread) onCreateThread();
            else onOpenPatientPickerForNewThread();
          }}
          className="flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-primary/35 bg-primary/10 text-[12px] font-medium text-primary transition-colors hover:bg-primary/15"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          New conversation
        </button>

        {totalThreads > 5 ? (
          <label className="relative block">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations…"
              aria-label="Search conversations"
              className="w-full rounded-lg border border-sidebar-border bg-sidebar-accent/30 py-1.5 pl-7 pr-2 text-[11px] text-sidebar-foreground placeholder:text-text-tertiary focus:outline-hidden focus:ring-2 focus:ring-primary/40"
            />
          </label>
        ) : null}

        {flatThreads.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-sidebar-border px-3 py-6 text-center">
            <MessageSquare className="h-5 w-5 text-text-tertiary" aria-hidden />
            <p className="text-[11px] leading-relaxed text-text-tertiary">
              {search.trim() ? 'No conversations match your search.' : emptyCopy}
            </p>
          </div>
        ) : patientLocked ? (
          // One patient, one list — newest first.
          <ul className="space-y-1.5">
            {flatThreads.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                active={t.id === currentThreadId}
                onSelect={() => onSelectThread(t.id)}
                onDelete={() => onDeleteThread(t.id)}
              />
            ))}
          </ul>
        ) : (
          groups.map((group) => (
            <div key={group.patientLabel} className="space-y-1.5">
              <p className={sidebar.sectionLabel}>{group.patientLabel}</p>
              <ul className="space-y-1.5">
                {group.threads.map((t) => (
                  <ThreadRow
                    key={t.id}
                    thread={t}
                    active={t.id === currentThreadId}
                    onSelect={() => onSelectThread(t.id)}
                    onDelete={() => onDeleteThread(t.id)}
                  />
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
  );

  if (asSidebar) {
    return (
      <div style={sidebarWidth ? { width: `${sidebarWidth}px` } : undefined} className={sidebar.root}>
        {header(false)}
        {body}
      </div>
    );
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="left"
        showCloseButton={false}
        className={cn(sidebar.root, 'w-[min(100vw,22rem)] max-w-full gap-0')}
      >
        {header(true)}
        {body}
      </SheetContent>
    </Sheet>
  );
}
